import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { createFormFromBlob, createFormFromFile } from "../helpers/Utilities";
import {
  notifySlideOut,
  selectCroppedTarget,
  setCroppedImage,
  setCroppedTarget,
  uploadFile,
} from "../reducers/utilitySlice";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { mediumDelay } from "../helpers/JSONapi";
import { getNotificationItemInfo } from "../helpers/Converters";

export const useFormValidation = (initialValid) => {
  const [isValid, setIsValid] = useState(initialValid);
  const [warnings, setWarnings] = useState([]);
  let context = useMemo(() => ({}), []);
  context.validationFailed = (e) => {
    if (isValid) {
      setIsValid(false);
      setWarnings([...warnings, e]);
    }
  };
  return [context, isValid, setIsValid];
};

export const useFormDataValidation = (settings) => {
  const [isValid, setIsValid] = useState(settings?.initialValid);
  const [warnings, setWarnings] = useState([]);
  let context = useMemo(() => ({}), []);
  const dispatch = useDispatch();
  context.validationFailed = (e) => {
    if (isValid) {
      setIsValid(false);
      setWarnings([...warnings, e]);
    }
  };
  context.updateData = (value, property) => {
    if (settings.data && settings.setData) {
      setIsValid(true);
      let update = { ...settings.data };
      update[property] = value;
      dispatch(settings.setData(update));
    }
  };
  context.getData = () => {
    return settings?.data;
  };
  return [context, isValid, setIsValid];
};

export const useFileSizeValidator = (maxTotalBytes) => {
  const dispatch = useDispatch();

  return (files) => {
    if (!maxTotalBytes) return true;
    const total = (files ?? []).reduce((sum, f) => sum + (f?.size ?? 0), 0);
    if (total <= maxTotalBytes) return true;
    const bannerText = `The total size of files exceeds the ${maxTotalBytes /
      1000000}MB limit. Please reduce the total file size and try again.`;
    dispatch(
      notifySlideOut({
        header: getNotificationItemInfo(bannerText, faExclamationCircle),
        state: "warning",
        timeout: mediumDelay,
      }),
    );
    return false;
  };
};

export const useInlineImageUploader = (maxBytes) => {
  const dispatch = useDispatch();

  return async (html) => {
    if (!html) return { html };
    const doc = new DOMParser().parseFromString(html, "text/html");
    const dataUrls = Array.from(
      new Set(
        Array.from(doc.querySelectorAll('img[src^="data:"]')).map((img) =>
          img.getAttribute("src"),
        ),
      ),
    );
    if (dataUrls.length === 0) return { html };

    let updatedHtml = html;
    for (const dataUrl of dataUrls) {
      const blob = await (await fetch(dataUrl)).blob();
      if (maxBytes && blob.size > maxBytes) {
        dispatch(
          notifySlideOut({
            header: getNotificationItemInfo(
              `Images added alongside text must be under ${maxBytes /
                1000000}MB. Remove or replace any that are too large and try saving again.`,
              faExclamationCircle,
            ),
            state: "warning",
            timeout: mediumDelay,
          }),
        );
        return { uploadFailed: true };
      }
      const extension = blob.type?.split("/")?.[1] ?? "png";
      const response = await dispatch(
        uploadFile(createFormFromBlob(blob, `pasted-image.${extension}`)),
      );
      const location = response?.payload?.data?.location;
      if (!location) {
        dispatch(
          notifySlideOut({
            header: getNotificationItemInfo(
              "An image couldn't be uploaded. Please try again.",
              faExclamationCircle,
            ),
            state: "warning",
            timeout: mediumDelay,
          }),
        );
        return { uploadFailed: true };
      }
      const hostedUrl = `${process.env.REACT_APP_GOLFSTATUS_BASE_API_URL}${location}`;
      updatedHtml = updatedHtml.split(dataUrl).join(hostedUrl);
    }
    return { html: updatedHtml };
  };
};

//Image forms
export const useImageSelector = (context, property, maxFileSize) => {
  const [imageReady, setImageReady] = useState(false);
  const croppedTarget = useSelector(selectCroppedTarget);
  const dispatch = useDispatch();

  const maxSizeBanner = () => {
    const bannerText = `This file exceeds the ${maxFileSize /
      1000000}MB size limit. Please upload a smaller file.`;
    return {
      header: getNotificationItemInfo(bannerText, faExclamationCircle),
      state: "warning",
      timeout: mediumDelay,
    };
  };

  const setLogo = (files) => {
    const file = files[0];
    if (maxFileSize && file?.size > maxFileSize) {
      dispatch(notifySlideOut(maxSizeBanner()));
      return;
    }

    if (file) {
      context.updateData(URL.createObjectURL(file), property);
      const data = createFormFromFile(file);
      let target = {
        ...croppedTarget,
        id: context.getData().id,
      };
      target[property] = data;
      setImageReady(false);
      dispatch(setCroppedTarget(target));
    }
  };

  const removeLogo = () => {
    let target = {
      ...croppedTarget,
    };
    target[property] = undefined;
    dispatch(setCroppedTarget(target));
    dispatch(setCroppedImage(target));
    context.updateData("", property);
  };

  const getLogo = () => {
    if (
      context?.getData?.()?.[property] &&
      context?.getData?.()?.[property] !== "/logos/default/missing.png"
    ) {
      return [{ url: context?.getData?.()?.[property], type: "image" }];
    }
    return [];
  };

  const uploadSelectedFile = () => {
    if (croppedTarget?.id === context?.getData()?.id) {
      if (croppedTarget?.[property]) {
        dispatch(uploadFile(croppedTarget?.[property])).then?.((response) => {
          setImageReady(response?.payload?.data?.location ?? false);
        });
      } else if (context?.getData?.()?.[property]) {
        setImageReady(true);
      }
    }
  };

  return [
    imageReady,
    setImageReady,
    setLogo,
    removeLogo,
    getLogo,
    uploadSelectedFile,
  ];
};

//crop image forms
export const useImageCropper = (context, property) => {
  const [croppedImg, setCroppedImg] = useState();
  const [croppedImgBlob, setCroppedImageBlob] = useState();
  const [showCroppedImage, setShowCroppedImage] = useState(false);

  const croppedTarget = useSelector(selectCroppedTarget);
  const dispatch = useDispatch();

  useEffect(() => {
    const editor = document.getElementById("image-editor");
    editor.focus();
  }, []);

  const saveLogo = () => {
    let target = {
      ...croppedTarget,
      id: context?.getData?.()?.id,
    };
    target[property] = createFormFromBlob(croppedImgBlob, "png");
    dispatch(setCroppedTarget(target));
    context.updateData(croppedImg, property);
  };

  context.setCroppedDataURL = (dataURL) => {
    setCroppedImg(dataURL);
  };
  context.setCroppedBlob = (blob) => {
    setCroppedImageBlob(blob);
  };

  const applyCrop = () => {
    setShowCroppedImage(true);
  };
  const undoCrop = () => {
    setShowCroppedImage(false);
  };

  return [
    croppedImg,
    showCroppedImage,
    saveLogo,
    applyCrop,
    undoCrop,
    croppedImgBlob,
    croppedTarget
  ];
};
